import {
  Box,
  ListItem,
  ListItemAvatar,
  ListItemText,
  MenuProps,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { Theme, useTheme } from '@mui/material/styles';
import { useCallback } from 'react';

import { useAppDispatch, useAppSelector } from '@/hooks/redux';
import { useTranslate } from '@/hooks/useTranslate';

import { selectLoggedInUser } from '@/redux/auth/selectors';
import { selectCanAbandonApplication } from '@/redux/configs';
import { setUserSelectedLanguage } from '@/redux/ui';

import { Tenant } from '@/types/common';
import { Language } from '@/types/i18n';

import { getDataTestId } from '@/utils/browser';
import { getFullName } from '@/utils/string';
import { isRemediationCustomer } from '@/utils/user';

import { Icon } from '../Icon/Icon';
import { Avatar } from './Avatar';
import { UserMenuOption } from './User.interface';

import { MenuButton, MenuDivider, StyledMenu } from './User.styled';

type UserMenuProps = {
  onOptionClick: (option: UserMenuOption) => void;
} & MenuProps;

export function UserMenu({ onOptionClick, ...props }: UserMenuProps) {
  const user = useAppSelector(selectLoggedInUser);
  const theme = useTheme();
  const { t, language } = useTranslate();
  const dispatch = useAppDispatch();
  const canAbandonApplication = useAppSelector(selectCanAbandonApplication);

  const handleLanguageChange = useCallback(
    (_: React.MouseEvent<HTMLElement>, newLanguage: Language | null) => {
      if (newLanguage && user?.language !== newLanguage) {
        dispatch(setUserSelectedLanguage(newLanguage));
      }
    },
    [dispatch, user?.language],
  );

  return (
    <StyledMenu {...getDataTestId('user-header-menu')} {...props}>
      <Box p={2} pb={0}>
        <ListItem>
          <ListItemAvatar>
            <Avatar size={40} src={user?.picture}>
              {getFullName(user)}
            </Avatar>
          </ListItemAvatar>
          <ListItemText
            primary={
              <Typography variant="body2Bold">{getFullName(user)}</Typography>
            }
            secondary={
              <Typography variant="caption" color={theme.palette.grey[800]}>
                @{user?.username}
              </Typography>
            }
          ></ListItemText>
        </ListItem>
      </Box>

      <Stack direction="column" alignItems="stretch" p={2} pt={1.5} gap={0.5}>
        <MenuDivider />
        {user?.tenant === Tenant.Suriname && (
          <>
            <Box p={1}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
              >
                <Stack direction="row" alignItems="center" gap={1}>
                  <Icon
                    name="globe-simple"
                    color={(theme: Theme) => theme.palette.grey[700]}
                  />
                  <Typography variant="body2" color={theme.palette.grey[1000]}>
                    {t('common.language')}
                  </Typography>
                </Stack>
                <ToggleButtonGroup
                  value={language}
                  exclusive
                  onChange={handleLanguageChange}
                  aria-label="language"
                  size="small"
                  {...getDataTestId('language-toggle-group')}
                >
                  <ToggleButton
                    value={Language.English}
                    {...getDataTestId('language-option-en')}
                  >
                    {t('language.en')}
                  </ToggleButton>
                  <ToggleButton
                    value={Language.Dutch}
                    {...getDataTestId('language-option-nl')}
                  >
                    {t('language.nl')}
                  </ToggleButton>
                </ToggleButtonGroup>
              </Stack>
            </Box>
            <MenuDivider />
          </>
        )}
        {!isRemediationCustomer(user) && canAbandonApplication && (
          <>
            <MenuButton onClick={() => onOptionClick('cancel-application')}>
              {t('common.cancelApplication')}
            </MenuButton>
            <MenuDivider />
          </>
        )}

        <MenuButton
          onClick={() => onOptionClick('sign-out')}
          startIcon={<Icon name="sign-out" />}
          {...getDataTestId('sign-out-button')}
        >
          {t('userMenu.button.signOut')}
        </MenuButton>
      </Stack>
    </StyledMenu>
  );
}
